class MapriskDrawingManager {
	constructor(map, eventBus, userOverlays) {
		if (!map) {
			throw new Error("Init error: map instance must be provided.");
		}

		this.map = map;
		this.eventBus = eventBus;
		this.userOverlays = userOverlays;
		this.mapProjection = map.getProjection();

		this.store = {
			activeDrawingEvent: null,
			activeListeners: [],
			activeMode: null,
			activeOverlay: null,
			drawSessionId: 0
		};

		this.defaultStyleOptions = {
			fillColor: "#fd8421",
			fillOpacity: 0.35,
			strokeColor: "#fd8421",
			strokeOpacity: 1,
			strokeWeight: 2
		};

		this.styleOptions = {
			fillColor: "#fd8421",
			fillOpacity: 0.35,
			strokeColor: "#fd8421",
			strokeOpacity: 1,
			strokeWeight: 2
		};

		this.initialCursor = map.getDiv().style.cursor;
		this.clickTimeout = null;
	}

	setDrawingMode(mode) {
		if (this.userIsDrawing()) {
			this.cancelDrawing();
		}

		this.store.drawSessionId++;

		this.map.setOptions({ draggableCursor: "crosshair" });

		switch (mode) {
			case "circle":
				this.store.activeMode = "circle";
				this.drawCircle();
				break;

			case "marker":
				this.store.activeMode = "marker";
				this.drawMarker();
				break;

			case "polygon":
				this.store.activeMode = "polygon";
				this.drawPolygon();
				break;

			case "polyline":
				this.store.activeMode = "polyline";
				this.drawPolyline();
				break;

			case "rectangle":
				this.store.activeMode = "rectangle";
				this.drawRectangle();
				break;

			default:
				break;
		}
	}

	setOptions(options) {
		this.styleOptions = Object.assign({}, this.styleOptions, options);
	}

	useDefaultOptions() {
		this.styleOptions = this.defaultStyleOptions;
	}

	allowMapZoomOnDoubleClick(flag) {
		if (flag !== false) {
			flag = true;
		}

		this.map.setOptions({ disableDoubleClickZoom: !flag });
	}

	allowOverlayClick(flag) {
		if (flag !== false) {
			flag = true;
		}

		for (let overlay in this.userOverlays) {
			if (!Array.isArray(this.userOverlays[overlay])) {
				continue;
			}

			this.userOverlays[overlay].forEach((el) => {
				el.setOptions({ clickable: flag });
			});
		}
	}

	cancelDrawing() {
		this.store.activeOverlay?.setMap(null);
		this.finishDrawing();
	}

	finishDrawing() {
		this.store.drawSessionId++;

		this.allowOverlayClick(true);

		if (this.clickTimeout) {
			clearTimeout(this.clickTimeout);
			this.clickTimeout = null;
		}

		this.store.activeOverlay = null;

		setTimeout(() => {
			this.allowMapZoomOnDoubleClick(true);
		}, 250);

		// Clear active drawing event
		if (this.store.activeDrawingEvent) {
			this.store.activeDrawingEvent.remove();
			this.store.activeDrawingEvent = null;
		}

		// Clear active listeners
		for (let i = 0; i < this.store.activeListeners.length; i++) {
			this.store.activeListeners[i].remove();
		}

		this.store.activeListeners = [];
		this.map.setOptions({ draggableCursor: "pointer" });
		this.map.getDiv().style.cursor = this.initialCursor;
	}

	userIsDrawing() {
		return this.store.activeDrawingEvent !== null;
	}

	drawMarker() {
		const self = this;
		let marker;

		self.allowMapZoomOnDoubleClick(false);

		// disable mousemove event listener on overlays to prevent interference with drawing
		self.allowOverlayClick(false);

		// Add a one-time click listener to the map to add a marker
		self.store.activeDrawingEvent = google.maps.event.addListenerOnce(
			self.map,
			"click",
			(e) => {
				const poi = { lat: e.latLng.lat(), lng: e.latLng.lng() };

				marker = new google.maps.Marker({
					map: self.map,
					position: poi,
					draggable: false,
					editable: false,
					clickable: false
				});
				self.store.activeOverlay = marker;
				marker.setMap(self.map);

				self.eventBus.publish("map:drawing:overlaycomplete", {
					type: "marker",
					overlay: marker
				});

				self.finishDrawing();

				// recursively call this function to allow for drawing multiple
				// circles without re-selecting the tool
				self.setDrawingMode("marker");
			}
		);

		// add a right-click listener to cancel the drawing event
		let rightClickListener = google.maps.event.addListenerOnce(
			self.map,
			"rightclick",
			() => {
				self.cancelDrawing();
			}
		);
		self.store.activeListeners.push(rightClickListener);
	}

	drawCircle() {
		const self = this;
		const sessionId = self.store.drawSessionId;
		let circle;

		self.allowMapZoomOnDoubleClick(false);

		// disable mousemove event listener on overlays to prevent interference with drawing
		self.allowOverlayClick(false);

		// Add a one-time click listener to the map to begin drawing
		self.store.activeDrawingEvent = google.maps.event.addListenerOnce(
			self.map,
			"click",
			(e) => {
				if (self.store.drawSessionId !== sessionId) {
					return;
				}

				const center = { lat: e.latLng.lat(), lng: e.latLng.lng() };

				circle = new google.maps.Circle({
					strokeColor: self.styleOptions.strokeColor,
					strokeOpacity: self.styleOptions.strokeOpacity,
					strokeWeight: self.styleOptions.strokeWeight,
					fillColor: self.styleOptions.fillColor,
					fillOpacity: self.styleOptions.fillOpacity,
					map: self.map,
					center: center,
					radius: 0,
					draggable: false,
					editable: false,
					clickable: false
				});
				self.store.activeOverlay = circle;
				circle.setMap(self.map);

				// add a right-click listener to cancel the drawing event
				let rightClickListener = google.maps.event.addListenerOnce(
					self.map,
					"rightclick",
					() => {
						self.cancelDrawing();
					}
				);
				self.store.activeListeners.push(rightClickListener);

				// add a mouse move listener to update the radius of the circle as the user moves the mouse
				let drawMoveListener = google.maps.event.addListener(
					self.map,
					"mousemove",
					(e) => {
						let radius = google.maps.geometry.spherical.computeDistanceBetween(
							new google.maps.LatLng(center),
							e.latLng
						);

						circle.setRadius(radius);
					}
				);
				self.store.activeListeners.push(drawMoveListener);

				// add one more one-time click listener to complete the drawing of the circle
				let closeClickListener = google.maps.event.addListenerOnce(
					self.map,
					"click",
					(e) => {
						circle.setOptions({ clickable: true });

						self.eventBus.publish("map:drawing:overlaycomplete", {
							type: "circle",
							overlay: circle
						});

						self.finishDrawing();

						// recursively call this function to allow for drawing multiple
						// circles without re-selecting the tool
						self.setDrawingMode("circle");
					}
				);
				self.store.activeListeners.push(closeClickListener);
			}
		);
	}

	drawPolygon(
		paths = [],
		polygon = null,
		moveListener = null,
		rightClickListener = null,
		doubleClickListener = null
	) {
		const self = this;
		const sessionId = self.store.drawSessionId;

		self.allowMapZoomOnDoubleClick(false);

		// disable native mouse event listeners on overlays to prevent interference with drawing
		self.allowOverlayClick(false);

		// Add a click listener to the map to begin drawing
		self.store.activeDrawingEvent = google.maps.event.addListenerOnce(
			self.map,
			"click",
			(e) => {
				// Delay to wait for double click
				self.clickTimeout = setTimeout(() => {
					if (self.store.drawSessionId !== sessionId) {
						return;
					}

					/*
						If at least 2 points are drawn,	add doubleClickListener to close polygon.
						If user clicks near the first vertex, then complete the drawing and exit
					*/
					if (polygon?.getPath()?.getLength() >= 2) {
						if (!doubleClickListener) {
							doubleClickListener = self.polygonDoubleClickListener(polygon);
						}

						let nearFirstVertex = self.checkIfNearFirstVertex(
							e.latLng,
							polygon
						);

						if (nearFirstVertex) {
							self.closePolygon(polygon, true);

							return;
						}
					}

					const clickPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
					paths.push(clickPoint);

					if (!polygon) {
						polygon = new google.maps.Polygon({
							strokeColor: self.styleOptions.strokeColor,
							strokeOpacity: self.styleOptions.strokeOpacity,
							strokeWeight: self.styleOptions.strokeWeight,
							fillColor: self.styleOptions.fillColor,
							fillOpacity: self.styleOptions.fillOpacity,
							map: self.map,
							paths: paths,
							draggable: false,
							editable: false,
							clickable: false
						});
						self.store.activeOverlay = polygon;
						polygon.setMap(self.map);

						rightClickListener = self.polygonRightClickListener();
					}

					if (!moveListener) {
						moveListener = self.polygonMoveListener(paths, polygon);
					}

					self.drawPolygon(
						paths,
						polygon,
						moveListener,
						rightClickListener,
						doubleClickListener
					);
				}, 250);
			}
		);
	}

	polygonDoubleClickListener(polygon) {
		const self = this;
		let doubleClickListener = google.maps.event.addListenerOnce(
			self.map,
			"dblclick",
			() => {
				clearTimeout(self.clickTimeout);
				self.closePolygon(polygon, false);
			}
		);

		self.store.activeListeners.push(doubleClickListener);
		return doubleClickListener;
	}

	polygonRightClickListener() {
		const self = this;

		let rightClickListener = google.maps.event.addListenerOnce(
			self.map,
			"rightclick",
			() => {
				self.cancelDrawing();
			}
		);

		self.store.activeListeners.push(rightClickListener);
		return rightClickListener;
	}

	polygonMoveListener(paths, polygon) {
		const self = this;

		/*
			add a mouse move listener to draw a temporary line between the last polygon
			point and the current mouse position
		*/
		let drawMoveListener = google.maps.event.addListener(
			self.map,
			"mousemove",
			(e) => {
				let mousePos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
				let tmpPaths = paths.concat([mousePos]);

				// draw a temporary line between the last polygon point and the current mouse position
				polygon.setPaths(tmpPaths);

				// Check if mouse is near the first vertex
				let nearFirstVertex = self.checkIfNearFirstVertex(e.latLng, polygon);

				if (nearFirstVertex) {
					self.map.getDiv().style.cursor = "pointer";
				} else {
					self.map.getDiv().style.cursor = self.initialCursor;
				}
			}
		);
		self.store.activeListeners.push(drawMoveListener);

		return drawMoveListener;
	}

	closePolygon(polygon, removeLastVertex) {
		polygon.setOptions({ clickable: true });
		this.eventBus.publish("map:drawing:overlaycomplete", {
			type: "polygon",
			overlay: polygon
		});

		if (removeLastVertex === true) {
			// Remove the last point added to allow the polygon to auto-close
			// without requiring the user to click directly on the first vertex
			polygon.getPath().pop();
		}

		// re-call this function to allow for drawing multiple
		// polygons without re-selecting the tool
		this.finishDrawing();
		this.setDrawingMode("polygon");
	}

	drawPolyline(
		path = [],
		polyline = null,
		moveListener = null,
		rightClickListener = null,
		doubleClickListener = null
	) {
		const self = this;
		const sessionId = self.store.drawSessionId;

		self.allowMapZoomOnDoubleClick(false);

		// disable mousemove event listener on overlays to prevent interference with drawing
		self.allowOverlayClick(false);

		// Add a click listener to the map to begin drawing
		self.store.activeDrawingEvent = google.maps.event.addListenerOnce(
			self.map,
			"click",
			(e) => {
				// Set a delay to wait to see if user has double clicked
				self.clickTimeout = setTimeout(() => {
					if (self.store.drawSessionId !== sessionId) {
						return;
					}

					const clickPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
					path.push(clickPoint);

					if (!polyline) {
						polyline = new google.maps.Polyline({
							strokeColor: self.styleOptions.strokeColor,
							strokeOpacity: self.styleOptions.strokeOpacity,
							strokeWeight: self.styleOptions.strokeWeight,
							map: self.map,
							path: path,
							draggable: false,
							editable: false,
							clickable: false
						});
						self.store.activeOverlay = polyline;
						polyline.setMap(self.map);

						rightClickListener = self.polylineRightClickListener();
					}

					if (!doubleClickListener) {
						doubleClickListener = self.polylineDoubleClickListener(polyline);
					}

					if (!moveListener) {
						moveListener = self.polylineMoveListener(path, polyline);
					}

					self.drawPolyline(
						path,
						polyline,
						moveListener,
						rightClickListener,
						doubleClickListener
					);
				}, 250);
			}
		);
	}

	polylineDoubleClickListener(polyline) {
		const self = this;
		let doubleClickListener = google.maps.event.addListenerOnce(
			self.map,
			"dblclick",
			() => {
				clearTimeout(self.clickTimeout);
				self.endPolyline(polyline);
			}
		);

		self.store.activeListeners.push(doubleClickListener);
		return doubleClickListener;
	}

	polylineRightClickListener() {
		const self = this;

		let rightClickListener = google.maps.event.addListenerOnce(
			self.map,
			"rightclick",
			() => {
				self.cancelDrawing();
			}
		);

		self.store.activeListeners.push(rightClickListener);
		return rightClickListener;
	}

	polylineMoveListener(path, polyline) {
		const self = this;

		/*
			add a mouse move listener to draw a temporary line between the last polyline
			point and the current mouse position
		*/
		let drawMoveListener = google.maps.event.addListener(
			self.map,
			"mousemove",
			(e) => {
				let mousePos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
				let tmpPath = path.concat([mousePos]);

				// draw a temporary line between the last polyline point and the current mouse position
				polyline.setPath(tmpPath);
			}
		);
		self.store.activeListeners.push(drawMoveListener);

		return drawMoveListener;
	}

	endPolyline(polyline) {
		polyline.setOptions({ clickable: true });
		this.eventBus.publish("map:drawing:overlaycomplete", {
			type: "polyline",
			overlay: polyline
		});

		// re-call this function to allow for drawing multiple
		// polygons without re-selecting the tool
		this.finishDrawing();
		this.setDrawingMode("polyline");
	}

	drawRectangle() {
		const self = this;
		const sessionId = self.store.drawSessionId;
		let rectangle;

		self.allowMapZoomOnDoubleClick(false);

		// Add a one-time click listener to the map to begin drawing
		self.store.activeDrawingEvent = google.maps.event.addListenerOnce(
			self.map,
			"click",
			(e) => {
				if (self.store.drawSessionId !== sessionId) {
					return;
				}

				const startVertex = { lat: e.latLng.lat(), lng: e.latLng.lng() };

				rectangle = new google.maps.Rectangle({
					strokeColor: self.styleOptions.strokeColor,
					strokeOpacity: self.styleOptions.strokeOpacity,
					strokeWeight: self.styleOptions.strokeWeight,
					fillColor: self.styleOptions.fillColor,
					fillOpacity: self.styleOptions.fillOpacity,
					map: self.map,
					bounds: {
						north: startVertex.lat,
						south: startVertex.lat,
						east: startVertex.lng,
						west: startVertex.lng
					},
					draggable: false,
					editable: false,
					clickable: false
				});
				self.store.activeOverlay = rectangle;
				rectangle.setMap(self.map);

				// add a right-click listener to cancel the drawing event
				let rightClickListener = google.maps.event.addListenerOnce(
					self.map,
					"rightclick",
					() => {
						self.cancelDrawing();
					}
				);
				self.store.activeListeners.push(rightClickListener);

				// disable mousemove event listener on overlays to prevent interference with drawing
				self.allowOverlayClick(false);

				// add a mouse move listener to update the bounds of the rectangle as the user moves the mouse
				let drawMoveListener = google.maps.event.addListener(
					self.map,
					"mousemove",
					(e) => {
						let bounds = new google.maps.LatLngBounds();

						bounds.extend(startVertex);
						bounds.extend(e.latLng);

						rectangle.setBounds(bounds);
					}
				);
				self.store.activeListeners.push(drawMoveListener);

				// add one more one-time click listener to complete the drawing of the rectangle
				let closeClickListener = google.maps.event.addListenerOnce(
					self.map,
					"click",
					() => {
						rectangle.setOptions({ clickable: true });

						self.eventBus.publish("map:drawing:overlaycomplete", {
							type: "rectangle",
							overlay: rectangle
						});

						self.finishDrawing();

						// recursively call this function to allow for drawing multiple
						// rectanglea without re-selecting the tool
						self.setDrawingMode("rectangle");
					}
				);
				self.store.activeListeners.push(closeClickListener);
			}
		);
	}

	checkIfNearFirstVertex(latLng, shape) {
		let nearVertex = false;
		let firstVertex = shape.getPath().getAt(0);

		// determine if mouse pointer is within 10 pixels of any existing vertex to change cursor style
		const mousePixel = this.mapProjection.fromLatLngToPoint(latLng);
		const scale = Math.pow(2, this.map.getZoom());
		const vertexPixel = this.mapProjection.fromLatLngToPoint(firstVertex);
		const dx = (mousePixel.x - vertexPixel.x) * scale;
		const dy = (mousePixel.y - vertexPixel.y) * scale;
		const distance = Math.sqrt(dx * dx + dy * dy);

		if (distance <= 10) {
			// 10 pixels threshold
			nearVertex = true;
		}

		return nearVertex;
	}
}

module.exports = MapriskDrawingManager;
